import {
  SectionHeading,
  SelectField,
  TextField,
} from "@via/pages/projects/metadata/configuration/data-import/components/fields";
import type { ImportFormValues } from "@via/pages/projects/metadata/configuration/data-import/utils/form";
import {
  useVmetaCollections,
  useVmetaFieldsByCollectionIds,
} from "@via/pages/projects/metadata/metadata.api.tsx";
import type { FC } from "react";

type FieldOption = { name: string; label?: string; type?: string };
const API_FIELDS_LIST = "import-api-fields";
const addButton = "text-sm font-semibold text-indigo-600 hover:text-indigo-500";
const removeButton = "text-sm text-red-600 hover:text-red-500";

const MappingSection: FC<{
  canvasId: string;
  values: ImportFormValues;
  onChange: (patch: Partial<ImportFormValues>) => void;
  apiFields: string[];
  canLoadFields: boolean;
  onLoadFields: () => void;
  loadingFields: boolean;
  fieldsError: string | null;
}> = ({
  canvasId,
  values,
  onChange,
  apiFields,
  canLoadFields,
  onLoadFields,
  loadingFields,
  fieldsError,
}) => {
  const { data: collections } = useVmetaCollections(
    0,
    1000,
    [],
    [{ id: "projectID", value: canvasId }]
  );
  const collectionOptions = (collections?.data ?? []).map(
    (c: { _id: string; name: string; label?: string }) => ({
      value: String(c._id),
      label: c.label || c.name,
    })
  );
  const parentIds = [
    ...new Set(
      [
        ...values.parentRefs.map(r => r.collection),
        ...values.parentUpdates.map(u => u.collection),
      ].filter(Boolean)
    ),
  ];
  const ids = [values.collectionID, ...parentIds].filter(Boolean);
  const fieldResults = useVmetaFieldsByCollectionIds(ids);
  const fieldsOf = (collectionId: string): FieldOption[] => {
    const index = ids.indexOf(collectionId);
    return index < 0
      ? []
      : ((fieldResults[index]?.data?.data ?? []) as FieldOption[]);
  };
  const asOptions = (fields: FieldOption[]) =>
    fields.map(f => ({ value: f.name, label: f.label || f.name }));
  const targetFields = fieldsOf(values.collectionID);

  const setRow = <K extends "fields" | "parentRefs" | "parentUpdates">(
    key: K,
    index: number,
    patch: Partial<ImportFormValues[K][number]>
  ) =>
    onChange({
      [key]: (values[key] as object[]).map((row, i) =>
        i === index ? { ...row, ...patch } : row
      ),
    } as Partial<ImportFormValues>);
  const removeRow = (
    key: "fields" | "parentRefs" | "parentUpdates",
    index: number
  ) =>
    onChange({
      [key]: (values[key] as object[]).filter((_, i) => i !== index),
    } as Partial<ImportFormValues>);

  return (
    <section aria-labelledby="import-mapping-heading" className="space-y-4">
      <SectionHeading
        id="import-mapping-heading"
        title="Mapping"
        description="Which collection the records go into, and which API field fills each collection field."
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!canLoadFields || loadingFields}
          onClick={onLoadFields}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
          Load fields from API
        </button>
        {!canLoadFields && (
          <span className="text-xs text-gray-500">Save the import first.</span>
        )}
        {apiFields.length > 0 && (
          <span className="text-xs text-gray-500">
            {apiFields.length} API fields loaded.
          </span>
        )}
      </div>
      {fieldsError && (
        <p role="alert" className="text-sm text-red-700">
          {fieldsError}
        </p>
      )}
      <datalist id={API_FIELDS_LIST}>
        {apiFields.map(name => (
          <option key={name} value={name} />
        ))}
      </datalist>

      <SelectField
        id="import-target-collection"
        label="Target collection"
        value={values.collectionID}
        placeholder="Choose a collection"
        options={collectionOptions}
        onChange={collectionID =>
          onChange({ collectionID, refDB: "", fields: [], deletedField: "" })
        }
      />

      <div className="grid grid-cols-2 gap-3">
        <SelectField
          id="import-ref-db"
          label="Identifier in the collection"
          value={values.refDB}
          placeholder="Choose a field"
          options={asOptions(targetFields)}
          onChange={refDB => onChange({ refDB })}
        />
        <TextField
          id="import-ref-api"
          label="Identifier in the API"
          value={values.refAPI}
          list={API_FIELDS_LIST}
          onChange={refAPI => onChange({ refAPI })}
        />
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-gray-900">
          Field pairs
        </legend>
        {values.fields.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
            <SelectField
              id={`import-field-db-${i}`}
              label="Collection field"
              value={row.db}
              placeholder="Choose a field"
              options={asOptions(targetFields)}
              onChange={db => setRow("fields", i, { db })}
            />
            <TextField
              id={`import-field-api-${i}`}
              label="API field"
              value={row.api}
              list={API_FIELDS_LIST}
              onChange={api => setRow("fields", i, { api })}
            />
            <button
              type="button"
              className={removeButton}
              onClick={() => removeRow("fields", i)}>
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className={addButton}
          onClick={() =>
            onChange({ fields: [...values.fields, { db: "", api: "" }] })
          }>
          Add field pair
        </button>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-gray-900">
          Parent references
        </legend>
        {values.parentRefs.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-3">
            <SelectField
              id={`import-parent-ref-collection-${i}`}
              label="Parent collection"
              value={row.collection}
              placeholder="Choose"
              options={collectionOptions}
              onChange={collection =>
                setRow("parentRefs", i, { collection, field: "" })
              }
            />
            <SelectField
              id={`import-parent-ref-field-${i}`}
              label="Parent field"
              value={row.field}
              placeholder="Choose"
              options={asOptions(fieldsOf(row.collection))}
              onChange={field => setRow("parentRefs", i, { field })}
            />
            <TextField
              id={`import-parent-ref-api-${i}`}
              label="API field"
              value={row.api}
              list={API_FIELDS_LIST}
              onChange={api => setRow("parentRefs", i, { api })}
            />
            <button
              type="button"
              className={removeButton}
              onClick={() => removeRow("parentRefs", i)}>
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className={addButton}
          onClick={() =>
            onChange({
              parentRefs: [
                ...values.parentRefs,
                { collection: "", field: "", api: "" },
              ],
            })
          }>
          Add parent reference
        </button>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-gray-900">
          Parent updates
        </legend>
        {values.parentUpdates.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-end gap-3">
            <SelectField
              id={`import-parent-update-collection-${i}`}
              label="Parent collection"
              value={row.collection}
              placeholder="Choose"
              options={collectionOptions}
              onChange={collection =>
                setRow("parentUpdates", i, { collection, field: "" })
              }
            />
            <SelectField
              id={`import-parent-update-field-${i}`}
              label="Parent field"
              value={row.field}
              placeholder="Choose"
              options={asOptions(fieldsOf(row.collection))}
              onChange={field => setRow("parentUpdates", i, { field })}
            />
            <SelectField
              id={`import-parent-update-source-${i}`}
              label="Value from"
              value={row.source}
              options={[
                { value: "apiField", label: "API field" },
                { value: "string", label: "Fixed text" },
              ]}
              onChange={source =>
                setRow("parentUpdates", i, {
                  source: source === "string" ? "string" : "apiField",
                })
              }
            />
            <TextField
              id={`import-parent-update-value-${i}`}
              label={row.source === "string" ? "Text" : "API field"}
              value={row.value}
              list={row.source === "string" ? undefined : API_FIELDS_LIST}
              onChange={value => setRow("parentUpdates", i, { value })}
            />
            <button
              type="button"
              className={removeButton}
              onClick={() => removeRow("parentUpdates", i)}>
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className={addButton}
          onClick={() =>
            onChange({
              parentUpdates: [
                ...values.parentUpdates,
                { collection: "", field: "", source: "apiField", value: "" },
              ],
            })
          }>
          Add parent update
        </button>
      </fieldset>

      <SelectField
        id="import-deleted-field"
        label="Deleted flag field"
        value={values.deletedField}
        placeholder="None"
        options={asOptions(targetFields.filter(f => f.type === "Boolean"))}
        onChange={deletedField => onChange({ deletedField })}
      />
    </section>
  );
};

export default MappingSection;
